import { create } from 'zustand';

/**
 * Lead Scanner client state (Key Technical Decision 13): one module-scope
 * zustand store, plain `create<State>((set, get) => ({...}))` form with an
 * exported `initialState`, per the repo's zustand convention (root
 * CLAUDE.md — "Zustand for Module-Scope Client State").
 *
 * U3 (this unit) only needs the rep↔event mapper's staged draft. Later units
 * (Home's scans list, active-scan id, view route — Key Technical Decision
 * 13) add fields to this same store rather than creating a second one.
 *
 * ─── The staged draft (R4) ───────────────────────────────────────────────
 * Ports the prototype's `mapDraft()`/`savedKeys()`/`mapDiff()` model
 * (docs/lead-scanner-prototype.html, mapper section ~lines 1688-1711) — the
 * DIFF LOGIC, never the DOM code:
 *
 *   const mkey = (email, cid) => email + '|' + cid;
 *   const savedKeys = () => new Set(MAPPINGS.map(m => mkey(m.email, m.campaignId)));
 *   function mapDraft(){ if(!state.mapDraft) state.mapDraft = savedKeys(); return state.mapDraft; }
 *
 * `savedKeys` here mirrors the server's last-known-committed mappings (kept
 * fresh by `syncMappings()`, called whenever `/mappings` is (re)fetched).
 * `mapDraft` is the staged, edit-in-place working set: `null` until the
 * first `syncMappings()` call seeds it from `savedKeys` — exactly the
 * prototype's lazy `if(!state.mapDraft) state.mapDraft = savedKeys()`.
 * BOTH lenses (By event / By person) read and write this ONE set; the lens
 * switch (`setMapLens`) and the search text (`setMapQuery`) are rendering
 * concerns only and never touch `mapDraft` (R4's "lens-switch loses no
 * edits" requirement — see the store test for a same-reference proof, not
 * just a same-contents one).
 *
 * A background re-sync (e.g. a query refetch while the rep still has
 * unsaved edits open) must never silently overwrite an in-progress draft,
 * but it must not ignore it either: `syncMappings` (re)seeds `mapDraft`
 * wholesale only when it is `null` (nothing staged yet), and otherwise
 * RECONCILES the remote delta into the
 * open draft (`discardMapDraft` resets to a copy of `savedKeys`, not to
 * `null`, so it also goes through the reconcile path) — see its own doc
 * comment for the "locally touched" rule
 * (followups §11.6: leaving the draft untouched wholesale let the next Save
 * send a `remove` for a mapping the rep never touched, silently reverting a
 * teammate's change). A per-card filter text / open state
 * for each `CollapsibleCard` is purely local UI state owned by
 * `RepEventMapper.tsx` itself (keyed per event/person, never shared, never
 * needed by a test) — it does not belong in this cross-cutting store.
 */

export interface MappingPair {
  email: string;
  campaignId: string;
}

/** `email|campaignId` composite key — prototype's `mkey()`. Plain string
 *  join (not JSON) so `Set<string>` membership checks/toggles stay O(1)
 *  without a stringify/parse round trip. */
export function mkey(email: string, campaignId: string): string {
  return `${email}|${campaignId}`;
}

function pairFromKey(key: string): MappingPair {
  const cut = key.indexOf('|');
  return { email: key.slice(0, cut), campaignId: key.slice(cut + 1) };
}

/** Prototype's `savedKeys()`, parameterized on the mappings array instead of
 *  reading a module-level `MAPPINGS` global. */
export function savedKeysFromMappings(mappings: MappingPair[]): Set<string> {
  return new Set(mappings.map((m) => mkey(m.email, m.campaignId)));
}

export interface MapDiff {
  added: MappingPair[];
  removed: MappingPair[];
}

/** Prototype's `mapDiff()` — the exact set this unit's `PUT /mappings` body
 *  ({add, remove}) is built from, and what the sticky bar's pending count
 *  (`added.length + removed.length`) reads. Pure — no store access — so the
 *  PUT-body builder and the pending-count display can both call it directly
 *  against whatever `mapDraft`/`savedKeys` snapshot they're holding. */
export function computeMapDiff(draft: Set<string>, saved: Set<string>): MapDiff {
  const added: MappingPair[] = [];
  for (const key of draft) if (!saved.has(key)) added.push(pairFromKey(key));

  const removed: MappingPair[] = [];
  for (const key of saved) if (!draft.has(key)) removed.push(pairFromKey(key));

  return { added, removed };
}

export type MapLens = 'events' | 'people';

/**
 * Home / Scan-review / Settings view routing (R5-R7, U4). A plain string
 * union (not a nested object) so it's trivially serializable/comparable and
 * URL-shaped (`scan:<id>`) without a separate parse step for the common
 * case — `activeScanIdFromView` does the one place that needs the id back
 * out.
 *
 * `'all-submissions'` (U18/R20's admin-only cross-rep view) is gone — the
 * unified Scans page (plan plans/2026-08-26-001-lead-scanner-scans-page-plan.md)
 * replaces it with a real router route (`ScansPage`, `path="scans"` in
 * `index.tsx`), not a `view` value, since it needs its own shareable URL
 * (filters, the open scan) that this plain in-memory union can't carry.
 */
export type ViewRoute = 'home' | 'settings' | `scan:${string}`;

export function scanViewRoute(id: string): ViewRoute {
  return `scan:${id}`;
}

/** `null` for `'home'`/`'settings'` — there is no active scan on those views. */
export function activeScanIdFromView(view: ViewRoute): string | null {
  return view.startsWith('scan:') ? view.slice('scan:'.length) : null;
}

/**
 * The 1-draft-vs-many routing decision, as a discriminated union rather than
 * a bag of `view`/`scrollDrafts`/`toastMessage` fields (the pre-Scans-page
 * shape). The store itself must never own navigation (root CLAUDE.md's
 * zustand convention) — this result is consumed by `useCompleteIntake`
 * (`hooks/useCompleteIntake.ts`), which toasts the `'multi'` count.
 *
 * - `'single'` — exactly one new draft: opens straight to its review screen.
 *   Store-owned (`view`), matching the pre-existing behavior — no page
 *   navigation, no toast.
 * - `'multi'` — 2+ new drafts: no single scan to open. Live-QA round-3 bug
 *   1: this used to navigate to the Scans page (a real router route the
 *   store can't express as a `view` value); now the rep stays on Home, which
 *   records the batch in `intakeBatchIds` (`completeIntake` below) so Home's
 *   "New scans" list can render exactly these rows without navigating away.
 *   `count` is handed back so the caller can build its own toast copy
 *   without this pure function importing a toast library.
 * - `'none'` — defensive zero-ids case; no intake path should ever hit this.
 */
export type IntakeRouteResult =
  | { kind: 'single'; view: ViewRoute }
  | { kind: 'multi'; count: number }
  | { kind: 'none' };

/**
 * Prototype's `routeAfterIntake(newIds)` (docs/lead-scanner-prototype.html
 * ~line 782) — THE single place the 1-draft-vs-many routing decision lives,
 * shared by every intake path (manual add, "Scan badges", photo/paste
 * intake, the LinkedIn QR scanner). Ported as a pure decision function so it
 * is directly unit-testable and every intake path can share one
 * implementation. `useLeadScannerStore`'s `completeIntake` action is the
 * impure wiring for both the `'single'` case (it applies `view`) and the
 * `'multi'` case (it records `intakeBatchIds`); the `'multi'` case's TOAST
 * lives in `useCompleteIntake` instead, since a store must never call a
 * toast library itself (root CLAUDE.md's zustand convention).
 */
export function routeAfterIntake(ids: string[]): IntakeRouteResult {
  if (ids.length === 1) {
    return { kind: 'single', view: scanViewRoute(ids[0]) };
  }
  if (ids.length === 0) {
    // Defensive: no intake path should ever call this with zero ids (every
    // intake action creates at least one draft).
    return { kind: 'none' };
  }
  return { kind: 'multi', count: ids.length };
}

export interface LeadScannerState {
  /** Mirrors the server's last-known committed mappings. */
  savedKeys: Set<string>;
  /** The staged, edit-in-place working set. `null` until the first
   *  `syncMappings()` seeds it. */
  mapDraft: Set<string> | null;
  mapLens: MapLens;
  mapQuery: string;

  /**
   * (Re)seed `savedKeys` from a fresh `/mappings` GET.
   *
   * With no draft yet (`mapDraft === null`) it seeds the draft from the same
   * set. With a draft open it never overwrites the rep's edits wholesale;
   * instead it applies the REMOTE DELTA (what changed between the old and
   * new `savedKeys`) to the draft, skipping any key the rep has locally
   * touched — where "locally touched" is exactly
   * `draft.has(key) !== oldSaved.has(key)`, the same predicate the mapper's
   * "unsaved" badge renders from.
   *
   * So: a teammate's add appears in the draft rather than becoming a pending
   * `remove`, a teammate's removal disappears from the draft rather than
   * becoming a pending `add`, and the rep's own unsaved add/remove survives
   * either way (followups §11.6).
   */
  syncMappings: (mappings: MappingPair[]) => void;
  /** Toggle one email×campaignId pair in the draft. If the draft hasn't
   *  been seeded yet (shouldn't happen once the mapper has data, but kept
   *  defensive), it seeds from `savedKeys` first so the toggle still lands
   *  on a real working set. */
  toggleMapping: (email: string, campaignId: string) => void;
  /** Reset the draft back to the last-synced saved set — the mapper's
   *  Discard action. */
  discardMapDraft: () => void;
  /**
   * Called after a successful `PUT /mappings` with THE DIFF THAT WAS SENT
   * (the caller's captured `mutate()` argument), never with the live draft.
   *
   * Applies that diff to `savedKeys` — the server now holds exactly those
   * pairs on top of what it already had. Deliberately does NOT copy
   * `mapDraft`: if the rep toggled something else while the PUT was in
   * flight, the server never heard about it, so it must stay pending
   * (followups §11.5 — copying the live draft silently marked an unsent
   * edit as saved and dropped it forever).
   */
  commitSave: (diff: MapDiff) => void;
  /** Switching lenses is a render-only concern — never touches the draft
   *  (R4). Also clears `mapQuery`, matching the prototype's `setLens()`
   *  (each lens has its own placeholder — "Search events…" vs "Search
   *  people…" — so carrying old query text across the switch reads wrong). */
  setMapLens: (lens: MapLens) => void;
  setMapQuery: (query: string) => void;

  /** Current view route (Home / a scan's review screen / Settings). */
  view: ViewRoute;
  goHome: () => void;
  goSettings: () => void;
  openScan: (id: string) => void;
  /**
   * The impure wiring around `routeAfterIntake` — applies its decision's
   * `'single'` case to `view` (unchanged from before) and its `'multi'` case
   * to `intakeBatchIds` (live-QA round-3 bug 1: the rep now stays on Home
   * rather than navigating to the Scans page), returning the same result so
   * the caller can still toast the `'multi'` count. Toasting deliberately
   * does NOT happen here: a zustand store must never call a toast library
   * (root CLAUDE.md) — `hooks/useCompleteIntake.ts` is where it happens.
   */
  completeIntake: (ids: string[]) => IntakeRouteResult;

  /**
   * Scan ids of the most recent intake batch (photo or text), in creation
   * order — what Home's "New scans" list (live-QA round-3 bug 1) and the
   * Scans page's bulk `ProgressBar` count resolved-vs-total against ("the
   * client knows the batch ids", plan U5). Renamed from `photoBatchIds` when
   * `completeIntake` started recording every multi-scan batch, not just
   * photo-intake ones — a multi-result "Generate with text" batch has just as
   * much use for "show me exactly these rows" as a bulk photo scan does.
   *
   * Lives here rather than in a component's local state because a
   * single-scan intake routes straight to the scan-review screen, which on
   * a narrow viewport UNMOUNTS Home (and with it the picker) — component
   * state would be gone exactly when the progress display is still wanted.
   *
   * Never explicitly cleared: "all batch rows resolved" is derived from the
   * scans list, so a finished batch simply stops rendering a bar, and the
   * next batch overwrites the array. That avoids a clearing effect (and the
   * StrictMode lifetime-ref hazard that would come with one) for zero
   * behavioral loss.
   */
  intakeBatchIds: string[];
  setIntakeBatch: (ids: string[]) => void;
}

export const initialState: Pick<
  LeadScannerState,
  'savedKeys' | 'mapDraft' | 'mapLens' | 'mapQuery' | 'view' | 'intakeBatchIds'
> = {
  savedKeys: new Set<string>(),
  mapDraft: null,
  mapLens: 'events',
  mapQuery: '',
  view: 'home',
  intakeBatchIds: [],
};

export const useLeadScannerStore = create<LeadScannerState>((set, get) => ({
  ...initialState,

  syncMappings: (mappings) => {
    const saved = savedKeysFromMappings(mappings);
    set((state) => {
      // First load only — `discardMapDraft` resets the draft to a COPY of
      // `savedKeys`, never to `null`, so every later sync takes the
      // reconcile path below (where a discarded draft has nothing locally
      // touched and therefore tracks the server exactly). Unchanged by §11.6.
      if (state.mapDraft === null) return { savedKeys: saved, mapDraft: new Set(saved) };

      const oldSaved = state.savedKeys;
      const draft = state.mapDraft;
      const next = new Set(draft);
      let changed = false;

      // Remote ADDs (in the new saved set, absent from the old one). A key
      // the rep also added locally is "locally touched" — skip it; applying
      // the delta would be a no-op anyway, and after this sync it simply
      // stops being pending.
      for (const key of saved) {
        if (!oldSaved.has(key) && !draft.has(key)) {
          next.add(key);
          changed = true;
        }
      }

      // Remote REMOVALs (in the old saved set, absent from the new one). A
      // key the rep removed locally is "locally touched" — again a no-op,
      // and its pending removal resolves itself once `savedKeys` drops it.
      for (const key of oldSaved) {
        if (!saved.has(key) && draft.has(key)) {
          next.delete(key);
          changed = true;
        }
      }

      // Reference-stable when the server changed nothing the draft cares
      // about, so a poll-driven refetch can't churn every subscriber.
      return { savedKeys: saved, mapDraft: changed ? next : draft };
    });
  },

  toggleMapping: (email, campaignId) => {
    const key = mkey(email, campaignId);
    const current = get().mapDraft ?? new Set(get().savedKeys);
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    set({ mapDraft: next });
  },

  discardMapDraft: () => set((state) => ({ mapDraft: new Set(state.savedKeys) })),

  commitSave: (diff) =>
    set((state) => {
      const next = new Set(state.savedKeys);
      // `computeMapDiff` can never put the same key in both lists, so the
      // order of these two loops is immaterial.
      for (const p of diff.removed) next.delete(mkey(p.email, p.campaignId));
      for (const p of diff.added) next.add(mkey(p.email, p.campaignId));
      return { savedKeys: next };
    }),

  setMapLens: (lens) => set({ mapLens: lens, mapQuery: '' }),
  setMapQuery: (query) => set({ mapQuery: query }),

  goHome: () => set({ view: 'home' }),
  goSettings: () => set({ view: 'settings' }),
  openScan: (id) => set({ view: scanViewRoute(id) }),

  completeIntake: (ids) => {
    const result = routeAfterIntake(ids);
    if (result.kind === 'single') {
      // `intakeBatchIds` is also recorded here, not just on `'multi'` —
      // `EnrichmentManager.tsx`'s session-scoped auto-enrich gate
      // (`scansEligibleForSessionAutoEnrich`) requires batch membership, and
      // a single-scan intake (e.g. the LinkedIn QR scanner, which always
      // creates exactly one scan) would otherwise never become eligible.
      // Safe on Home's UI: `batchProgress.total > 1` is what gates the
      // "Reading badges" progress card, so a 1-item batch never renders it.
      set({ view: result.view, intakeBatchIds: ids });
    } else if (result.kind === 'multi') {
      // The rep stays on Home (live-QA round-3 bug 1) — record the batch so
      // Home's "New scans" list can render exactly these rows.
      set({ intakeBatchIds: ids });
    }
    // 'none' doesn't touch anything — the Scans-page navigation for 'multi'
    // used to be the caller's job (`useCompleteIntake`); that navigation is
    // gone (bug 1), but the toast still is.
    return result;
  },

  setIntakeBatch: (ids) => set({ intakeBatchIds: ids }),
}));

/**
 * Bulk-progress counts for the current intake batch (U5's optional
 * `ProgressBar` atop the drafts list; renamed alongside `intakeBatchIds`).
 * Pure so the "hide the bar once everything has landed" decision is one
 * testable expression rather than a conditional smeared through JSX.
 *
 * A batch id with no matching row (the rep deleted that draft mid-batch)
 * counts as resolved — it is certainly not still processing, and treating a
 * deleted row as pending would strand the bar at 99% forever.
 */
export function intakeBatchProgress(
  intakeBatchIds: string[],
  scans: { id: string; status: string }[],
): { total: number; resolved: number; done: boolean } {
  const statusById = new Map(scans.map((s) => [s.id, s.status]));
  const total = intakeBatchIds.length;
  const resolved = intakeBatchIds.filter((id) => statusById.get(id) !== 'processing').length;
  return { total, resolved, done: resolved >= total };
}
